import { useEffect, useRef, useState } from 'react'
import { FolderPlus, Loader2, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiError } from '@/lib/api-client'
import { useSaveQuickReply, type QuickReply } from '@/hooks/use-quick-replies'
import { quickReplyVariables } from './quick-reply-vars'
import { CATEGORY_MAX, categoryOptions, findCategory, normalizeCategoryName } from './quick-reply-category'

const CONTENT_MAX = 5000
/** Giá trị đặc biệt của ô chọn danh mục. */
const NO_CATEGORY = '__none__'
const NEW_CATEGORY = '__new__'

/** Popup thêm / sửa một câu trả lời nhanh. `item = null` → thêm mới. */
export function QuickReplyFormDialog({
  open,
  item,
  categories,
  canManageTeam,
  defaultCategory,
  onClose,
}: {
  open: boolean
  item: QuickReply | null
  categories: string[]
  canManageTeam: boolean
  /** Danh mục đang lọc ở trang — thêm mới thì điền sẵn. */
  defaultCategory?: string | null
  onClose: () => void
}) {
  const { t } = useTranslation('quickReplies')
  const save = useSaveQuickReply()
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [category, setCategory] = useState('')
  const [scope, setScope] = useState<'team' | 'personal'>('personal')
  /** Danh mục tạo bằng popup trong lúc mở thư viện (chưa có câu nào → máy chủ chưa biết); giữ qua các lần mở form. */
  const [createdCats, setCreatedCats] = useState<string[]>([])
  const [catDialog, setCatDialog] = useState(false)
  const contentRef = useRef<HTMLTextAreaElement>(null)
  const isEdit = !!item
  const catList = categoryOptions(categories, createdCats, category)

  function pickCategory(v: string) {
    if (v === NEW_CATEGORY) {
      // Đợi ô chọn đóng hẳn (trả focus) rồi mới mở popup tạo danh mục.
      setTimeout(() => setCatDialog(true), 0)
      return
    }
    setCategory(v === NO_CATEGORY ? '' : v)
  }

  function addCategory(name: string) {
    const existing = findCategory(catList, name)
    if (existing) {
      toast.info(t('form.categoryExists', { name: existing }))
      setCategory(existing)
    } else {
      setCreatedCats((prev) => [...prev, name])
      setCategory(name)
    }
    setCatDialog(false)
  }

  useEffect(() => {
    if (!open) return
    setTitle(item?.title ?? '')
    setContent(item?.content ?? '')
    setCategory(item?.category ?? defaultCategory ?? '')
    setScope(item ? (item.isPersonal ? 'personal' : 'team') : canManageTeam ? 'team' : 'personal')
  }, [open, item, defaultCategory, canManageTeam])

  /** Chèn biến vào đúng vị trí con trỏ trong ô nội dung. */
  function insertVar(key: string) {
    const token = `{{${key}}}`
    const el = contentRef.current
    const start = el?.selectionStart ?? content.length
    const end = el?.selectionEnd ?? content.length
    const next = content.slice(0, start) + token + content.slice(end)
    setContent(next)
    requestAnimationFrame(() => {
      el?.focus()
      el?.setSelectionRange(start + token.length, start + token.length)
    })
  }

  const canSave = !!title.trim() && !!content.trim() && content.length <= CONTENT_MAX && !save.isPending

  function submit() {
    if (!canSave) return
    save.mutate(
      {
        id: item?.id,
        input: {
          title: title.trim(),
          content: content.trim(),
          category: category.trim() || null,
          isPersonal: scope === 'personal',
        },
      },
      {
        onSuccess: () => {
          toast.success(isEdit ? t('form.updated') : t('form.added'))
          onClose()
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !save.isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('form.titleEdit') : t('form.titleAdd')}</DialogTitle>
          <DialogDescription>
            {t('form.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="qr-title">{t('form.titleLabel')}</Label>
            <Input
              id="qr-title"
              autoFocus
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('form.titlePlaceholder')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="qr-content">{t('form.contentLabel')}</Label>
            <Textarea
              id="qr-content"
              ref={contentRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('form.contentPlaceholder', { variable: '{{contact.firstName}}' })}
              rows={6}
            />
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">{t('form.insertVar')}</span>
              {quickReplyVariables().map((v) => (
                <button
                  key={v.key}
                  type="button"
                  onClick={() => insertVar(v.key)}
                  title={`{{${v.key}}}`}
                  className="rounded-md border px-1.5 py-0.5 text-xs text-primary transition-colors hover:bg-accent"
                >
                  {v.label}
                </button>
              ))}
              <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                {content.length}/{CONTENT_MAX}
              </span>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <div className="flex h-5 items-center justify-between gap-2">
                <Label htmlFor="qr-category">{t('form.categoryLabel')}</Label>
                <button
                  type="button"
                  onClick={() => setCatDialog(true)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  <Plus className="h-3 w-3" /> {t('form.createCategory')}
                </button>
              </div>
              <Select value={normalizeCategoryName(category) || NO_CATEGORY} onValueChange={pickCategory}>
                <SelectTrigger id="qr-category">
                  <SelectValue placeholder={t('form.pickCategory')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CATEGORY}>
                    <span className="text-muted-foreground">{t('form.uncategorized')}</span>
                  </SelectItem>
                  {catList.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                  <div role="separator" className="-mx-1 my-1 h-px bg-border" />
                  <SelectItem value={NEW_CATEGORY} className="font-medium text-primary">
                    {t('form.createCategoryNew')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex h-5 items-center">
                <Label>{t('form.scopeLabel')}</Label>
              </div>
              <Select
                value={scope}
                onValueChange={(v) => setScope(v as 'team' | 'personal')}
                // Đổi phạm vi mẫu đã có = chuyển chủ sở hữu → backend chưa hỗ trợ.
                disabled={isEdit}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="team" disabled={!canManageTeam}>
                    {t('form.scopeTeam')}
                  </SelectItem>
                  <SelectItem value="personal">{t('form.scopePersonal')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={submit} disabled={!canSave}>
            {save.isPending && <Loader2 className="animate-spin" />}
            {isEdit ? t('common:actions.saveChanges') : t('common:actions.add')}
          </Button>
        </DialogFooter>

        <CategoryCreateDialog
          open={catDialog}
          existing={catList}
          onClose={() => setCatDialog(false)}
          onCreate={addCategory}
        />
      </DialogContent>
    </Dialog>
  )
}

/** Popup nhỏ tạo danh mục — mở từ form thêm / sửa câu trả lời nhanh. Trùng tên (không phân biệt hoa thường) thì chọn danh mục có sẵn. */
function CategoryCreateDialog({
  open,
  existing,
  onClose,
  onCreate,
}: {
  open: boolean
  existing: string[]
  onClose: () => void
  onCreate: (name: string) => void
}) {
  const { t } = useTranslation('quickReplies')
  const [name, setName] = useState('')
  useEffect(() => {
    if (open) setName('')
  }, [open])

  const value = normalizeCategoryName(name)
  const dup = findCategory(existing, value)

  function submit() {
    if (value) onCreate(value)
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="h-4 w-4 text-primary" /> {t('category.title')}
          </DialogTitle>
          <DialogDescription>{t('category.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="qr-new-category">{t('category.nameLabel')}</Label>
          <Input
            id="qr-new-category"
            autoFocus
            value={name}
            maxLength={CATEGORY_MAX}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault()
                submit()
              }
            }}
            placeholder={t('category.namePlaceholder')}
          />
          {dup ? (
            <p className="text-xs text-amber-600 dark:text-amber-400">{t('category.duplicate', { name: dup })}</p>
          ) : existing.length > 0 ? (
            <p className="text-xs text-muted-foreground">{t('category.existing', { list: existing.join(' · ') })}</p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={submit} disabled={!value}>
            {dup ? t('category.pickExisting') : t('common:actions.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
